import { z } from 'zod';
import { ApiClient } from '../lib/api';
import {
  Client,
  ClientSchema,
  CreateClient,
  CreateClientSchema,
  UpdateClient,
  UpdateClientSchema,
  ClientLost,
  ClientLostSchema
} from '../models/client';

export class ClientService {
  private readonly api: ApiClient;

  constructor(baseURL: string, stage: string) {
    this.api = new ApiClient(`${baseURL}/${stage}/clients-service-${stage}`, stage);
  }

  private setDefaults(data: unknown): Client {
    return ClientSchema.parse(data);
  }

  async getAllClients(): Promise<Client[]> {
    const clients = await this.api.request(
      { method: 'GET', url: '/clients' },
      ClientSchema.array()
    );
    return clients.map(client => this.setDefaults(client));
  }

  async getClientById(id: number): Promise<Client> {
    const client = await this.api.request(
      { method: 'GET', url: `/clients/${id}` },
      ClientSchema
    );
    return this.setDefaults(client);
  }

  async getClientsByUserId(userId: number, cstatus?: string): Promise<Client[]> {
    let url = `/clients/user/${userId}`;
    if (cstatus) {
      url += `?cstatus=${encodeURIComponent(cstatus)}`;
    }
    
    const clients = await this.api.request(
      { method: 'GET', url },
      ClientSchema.array()
    );
    return clients.map(client => this.setDefaults(client));
  }

  async searchClients(query: string): Promise<Client[]> {
    const response = await this.api.request(
      { method: 'GET', url: `/clients/search?q=${encodeURIComponent(query)}` },
      z.object({
        searchTerm: z.string(),
        count: z.number(),
        clients: ClientSchema.array()
      })
    );
    return response.clients.map(client => this.setDefaults(client));
  }

  async putClientLost(id: number, data: ClientLost): Promise<Client> {
    const client = await this.api.request(
      {
        method: 'PUT',
        url: `/clients/lost/${id}`,
        data: ClientLostSchema.parse(data)
      },
      ClientSchema
    );
    return this.setDefaults(client);
  }

  async createClient(data: CreateClient): Promise<Client> {
    const client = await this.api.request(
      {
        method: 'POST',
        url: '/clients',
        data: CreateClientSchema.parse(data)
      },
      ClientSchema
    );
    return this.setDefaults(client);
  }

  async updateClient(id: number, data: UpdateClient): Promise<Client> {
    const client = await this.api.request(
      {
        method: 'PUT',
        url: `/clients/${id}`,
        data: UpdateClientSchema.parse(data)
      },
      ClientSchema
    );
    return this.setDefaults(client);
  }
} 